import { useEffect, useRef } from 'react';
import {
	Route,
	Routes,
	Navigate,
	useNavigate,
	useLocation,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSelf, refreshToken, logoutUser } from './slices/userSlice';
import LoginForm from './components/Login';
import Dashboard from './pages/Dashboard';
import ClientManagementHub from './pages/ClientManagementHub';
import Billing from './pages/Billing';
import jwService from './services/jwService';
import Inventory from './pages/Inventory';
import Leads from './pages/Leads';
import Orders from './pages/Orders';
import Clients from './pages/Clients';
import Settings from './pages/Settings';
import Clockin from './pages/Clockin';
import Repairs from './pages/Repairs';
import Pricing from './pages/Pricing';
import UserProfile from './pages/UserProfile';
import Installation from './pages/Installation';
import SendContract from './docusign/SendContract';
import Callback from './docusign/Callback';
import Calendar from './pages/Calendar';
import PitchCalculator from './pages/PitchCalculator';
import NewOrderPage from './pages/NewOrderPage';
import Privacy from './pages/Privacy';
import Support from './pages/Support';
import { Toaster } from 'react-hot-toast';
import BncLogoSpinner from './components/BncLogoSpinner';

function AuthGate({ children }) {
	const token = useSelector((state) => state.auth?.token) || localStorage.getItem('token');
	const rawCookieToken = jwService.getToken();
	const cookieToken = typeof rawCookieToken === 'string' && rawCookieToken.split('.').length === 3 ? rawCookieToken : null;
	const isAuthenticated = !!(token || cookieToken);
	if (!isAuthenticated) return <Navigate to="/" replace />;
	return children;
}

function BlockEmployeeGate({ children }) {
	const token = useSelector((state) => state.auth?.token) || localStorage.getItem('token');
	const selfAccount = useSelector((state) => state.auth?.self);
	const selfRole = selfAccount?.role;
	const rawCookieToken = jwService.getToken();
	const cookieToken = typeof rawCookieToken === 'string' && rawCookieToken.split('.').length === 3 ? rawCookieToken : null;
	const isAuthenticated = !!(token || cookieToken);
	if (!isAuthenticated) return <Navigate to="/" replace />;
	if (!selfRole) return <BncLogoSpinner size={80} />;
	if (selfRole === 'EMPLOYEE') return <Navigate to="/clock-in" replace />;
	return children;
}

function DefaultRedirect() {
	const token = useSelector((state) => state.auth?.token) || localStorage.getItem('token');
	const selfAccount = useSelector((state) => state.auth?.self);
	const selfRole = selfAccount?.role;
	const rawCookieToken = jwService.getToken();
	const cookieToken = typeof rawCookieToken === 'string' && rawCookieToken.split('.').length === 3 ? rawCookieToken : null;
	const isAuthenticated = !!(token || cookieToken);
	if (!isAuthenticated) return <Navigate to="/" replace />;
	if (selfRole === 'EMPLOYEE') return <Navigate to="/clock-in" replace />;
	return <Navigate to="/dashboard" replace />;
}

function App() {
	const token =
		useSelector((state) => state.auth?.token) || localStorage.getItem('token');
	const selfAccount = useSelector((state) => state.auth?.self);
	const selfRole = selfAccount?.role;
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const inactivityTimer = useRef(null);
	const selfFetchInFlight = useRef(false);
	const lastSelfFetchToken = useRef(null);
	const location = useLocation();
	const rawCookieToken = jwService.getToken();
	const cookieToken =
		typeof rawCookieToken === 'string' && rawCookieToken.split('.').length === 3
			? rawCookieToken
			: null;
	const isAuthenticated = !!(token || cookieToken);

	useEffect(() => {
		const effectiveToken = token || cookieToken;
		// Redirect to login page after 30 minutes of inactivity
		localStorage.setItem('redirectUrl', window.location.pathname);
		// use cookies service to redirect location
		document.cookie = `lastVisitedPage=${encodeURIComponent(
			location.pathname
		)}; path=/; max-age=${5 * 60 * 60}; Secure`;

		// Ensure localStorage token exists for API interceptors when cookie auth is used
		if (!token && cookieToken) {
			localStorage.setItem('token', cookieToken);
		}
		// Keep effect in sync with token sources (no local auth state here)
		void effectiveToken;
	}, [token, cookieToken, location.pathname]);

	useEffect(() => {
		// When auth is cookie-based, `token` may be empty until localStorage sync happens.
		// Still fetch self as long as we consider the session authenticated.
		if (!isAuthenticated || selfAccount) return;
		if (selfFetchInFlight.current) return;

		const effectiveToken = token || cookieToken;
		if (!effectiveToken) return;
		// Avoid refetching self for the same token forever (prevents refresh loops)
		if (lastSelfFetchToken.current === effectiveToken) return;
		lastSelfFetchToken.current = effectiveToken;

		selfFetchInFlight.current = true;
		dispatch(fetchSelf())
			.unwrap()
			.catch(() => {
				// If self can't be fetched for an authenticated session, treat it as logged out.
				dispatch(logoutUser());
			})
			.finally(() => {
			selfFetchInFlight.current = false;
		});
	}, [dispatch, isAuthenticated, selfAccount, token, cookieToken]);

	// Session Management: 15-minute inactivity timer and proactive token refresh
	useEffect(() => {
		const timeout = 14400000; // 4 hours

		const handleActivity = () => {
			if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

			inactivityTimer.current = setTimeout(() => {
				const quoteInProgress = localStorage.getItem('quoteInProgress') === 'true';
				if (!quoteInProgress && isAuthenticated) {
					// Logout due to inactivity
					dispatch(logoutUser());
					navigate('/');
				}
			}, timeout);
		};

		let refreshInterval;
		if (isAuthenticated) {
			const events = [
				'mousedown',
				'mousemove',
				'keydown',
				'scroll',
				'touchstart',
			];
			events.forEach((event) => document.addEventListener(event, handleActivity));
			
			// Proactive token refresh every 10 minutes
			refreshInterval = setInterval(() => {
				dispatch(refreshToken());
			}, 600000);

			handleActivity(); // Initialize timer

			return () => {
				events.forEach((event) =>
					document.removeEventListener(event, handleActivity)
				);
				if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
				if (refreshInterval) clearInterval(refreshInterval);
			};
		}
	}, [isAuthenticated, navigate, dispatch]);

	return (
		<div className="w-full">
			<Toaster position="top-right" reverseOrder={false} />
			<Routes>
				<Route path="/" element={<LoginForm />} />
				<Route path="/privacy" element={<Privacy />} />
				<Route path="/support" element={<Support />} />
				<Route
					path="/dashboard"
					element={
						<BlockEmployeeGate>
							<Dashboard />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/dashboard/client-management"
					element={
						<BlockEmployeeGate>
							<ClientManagementHub />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/profile"
					element={
						<AuthGate>
							<UserProfile />
						</AuthGate>
					}
				/>
				<Route
					path="/inventory"
					element={
						<BlockEmployeeGate>
							<Inventory />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/pricing"
					element={
						<BlockEmployeeGate>
							<Pricing />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/clients"
					element={
						<BlockEmployeeGate>
							<Clients />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/orders"
					element={
						<BlockEmployeeGate>
							<Orders />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/new-quote"
					element={
						<BlockEmployeeGate>
							<NewOrderPage />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/installations"
					element={
						<BlockEmployeeGate>
							<Installation />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/leads"
					element={
						<BlockEmployeeGate>
							<Leads />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/send-contract"
					element={
						<BlockEmployeeGate>
							<SendContract />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/repairs"
					element={
						<BlockEmployeeGate>
							<Repairs />
						</BlockEmployeeGate>
					}
				/>

				<Route
					path="/billing"
					element={
						<BlockEmployeeGate>
							<Billing />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/clock-in"
					element={
						<AuthGate>
							<Clockin />
						</AuthGate>
					}
				/>

				<Route
					path="/settings"
					element={
						<BlockEmployeeGate>
							<Settings />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/calendar"
					element={
						<BlockEmployeeGate>
							<Calendar />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/calculator"
					element={
						<BlockEmployeeGate>
							<PitchCalculator />
						</BlockEmployeeGate>
					}
				/>
				<Route
					path="/callback"
					element={
						<BlockEmployeeGate>
							<Callback />
						</BlockEmployeeGate>
					}
				/>

				<Route path="*" element={<DefaultRedirect />} />
			</Routes>
		</div>
	);
}

export default App;
